import { gql, useQuery } from "@apollo/client";
import {
  SalesFilter,
  Shimmers,
  StatsPageLoader,
  TableBodyComponent,
} from "components";
import lodash from "lodash";
import moment from "moment";
import numeral from "numeral";
import { FC, useMemo } from "react";
import { useSearch } from "react-location";
import { LocationGenerics } from "router/location";
import { getPeriod, useUpdatedSummaryData } from "utils";
import {getCustomerTypeServiceClasses} from "../../utils/get-customer-type";
const DATE_FORMAT = "YYYY-MM-DD";

const SUMMARY = gql`
  query GetSalesSummary(
    $region: ID
    $district: ID
    $tariffClass: ID
    $serviceClass: ServiceClass
    $serviceClasses: [ServiceClass]
    $month: PositiveInt
    $year: PositiveInt
    $type: SalesSummaryType
  ) {
    rows: getSalesSummary(
      region: $region
      district: $district
      tariffClass: $tariffClass
      serviceClass: $serviceClass
      serviceClasses: $serviceClasses
      month: $month
      year: $year
      type: $type
    ) {
      _id
      identifier
      billConsumptionValue
      outstandingAmount
      lifeLineAmount
      firstThresholdAmount
      secondThresholdAmount
      energyCharge
      serviceCharge
      energyPlusServiceCharge
      powerFactorSurcharge
      vatCharge
      nhilCharge
      getfundCharge
      streetLightCharge
      nationalElectrificationCharge
      billAmount
      paymentsAmount
      adjustmentAmount
      amountDue
      debtAmount
    }
  }
`;

const sortConsumptionBands = (consumptionBands: { identifier: string }[]) => {
  return consumptionBands.sort((a, b) => {
    // 1. Handle the "Negative" edge case
    const valA =
      a.identifier === "Negative"
        ? -1
        : parseInt(a.identifier.match(/\d+/)?.[0] || "-1");
    const valB =
      b.identifier === "Negative"
        ? -1
        : parseInt(b.identifier.match(/\d+/)?.[0] || "-1");

    // 2. Secondary check for "0" vs "0 - 300"
    // (Both have 0, so we check string length to put the range second)
    if (valA === valB) {
      return a.identifier.length - b.identifier.length;
    }

    return valA - valB;
  });
};

const Headers = [
  { label: "Consumption (KWH)", key: "billConsumptionValue", format: "0,0" },
  {
    label: "Opening Balance (GHS)",
    key: "outstandingAmount",
    format: "0,0.00",
  },
  { label: "Energy Charge (GHS)", key: "energyCharge", format: "0,0.00" },
  { label: "Service Charge (GHS)", key: "serviceCharge", format: "0,0.00" },
  {
    label: "Energy + Service Charge (GHS)",
    key: "energyPlusServiceCharge",
    format: "0,0.00",
  },
  {
    label: "Power Factor Surcharge (GHS)",
    key: "powerFactorSurcharge",
    format: "0,0.00",
  },
  { label: "VAT (GHS)", key: "vatCharge", format: "0,0.00" },
  { label: "NHIL (GHS)", key: "nhilCharge", format: "0,0.00" },
  { label: "GETFund (GHS)", key: "getfundCharge", format: "0,0.00" },
  {
    label: "Street Light (GHS)",
    key: "streetLightCharge",
    format: "0,0.00",
  },
  {
    label: "National Electrification (GHS)",
    key: "nationalElectrificationCharge",
    format: "0,0.00",
  },
  { label: "Bill Amount (GHS)", key: "billAmount", format: "0,0.00" },
  { label: "Payments Amount (GHS)", key: "paymentsAmount", format: "0,0.00" },
  {
    label: "Adjustment Amount (GHS)",
    key: "adjustmentAmount",
    format: "0,0.00",
  },
  { label: "Amount Due (GHS)", key: "amountDue", format: "0,0.00" },
  { label: "Debt Amount (GHS)", key: "debtAmount", format: "0,0.00" },
];

export const SalesConsumptionBandsPage: FC = () => {
  const searchParams = useSearch<LocationGenerics>();

  const filter = useMemo(
    () => ({
      region: searchParams.region || undefined,
      district: searchParams.district || undefined,
      month:
        moment(searchParams.fromDate || new Date(), DATE_FORMAT).month() + 1,
      year: moment(searchParams.fromDate || new Date(), DATE_FORMAT).year(),
      type: "ByConsumptionBand",
      ...searchParams?.filter,
      serviceClasses: getCustomerTypeServiceClasses(searchParams?.filter?.customerTypes),
    }),
    [searchParams],
  );

  const { data, loading, networkStatus } = useQuery(SUMMARY, {
    variables: filter,
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
  });

  const { records } = useUpdatedSummaryData(data);

  const dataRows = useMemo(() => {
    const consumptionBands = lodash
      .chain(records.rows)
      .filter(
        (row) => !["0 - 300", "301 - 600", "600+"].includes(row.identifier),
      )
      .value();
    const seniorConsumptionBands = lodash
      .chain(records.rows)
      .filter((row) =>
        ["Negative", "0", "0 - 300", "301 - 600", "600+"].includes(
          row.identifier,
        ),
      )
      .value();
    return [
      ...sortConsumptionBands(consumptionBands),
      {
        _id: "total",
        identifier: "Total",
        ...lodash.transform(
          consumptionBands,
          (acc: any, obj) => {
            lodash.forOwn(
              lodash.omit(obj, ["_id", "identifier"]),
              (value, key) => {
                acc[key] = (acc[key] || 0) + value;
              },
            );
          },
          {},
        ),
      },
      { _id: "space" },
      ...sortConsumptionBands(seniorConsumptionBands),
      {
        _id: "totalSenior",
        identifier: "Total",
        ...lodash.transform(
          seniorConsumptionBands,
          (acc: any, obj) => {
            lodash.forOwn(
              lodash.omit(obj, ["_id", "identifier"]),
              (value, key) => {
                acc[key] = (acc[key] || 0) + value;
              },
            );
          },
          {},
        ),
      },
    ];
  }, [records]);

  return (
    <div className="flex-1 flex flex-col mx-auto min-w-0 min-h-full">
      <SalesFilter filters={["serviceClasses", "tariffClasses", "customerTypes"]} />
      {loading ? (
        <StatsPageLoader />
      ) : (
        <div className="flex-1 min-h-full min-w-0 py-4 sm:py-6 px-6 overflow-hidden overflow-y-auto light flex flex-col">
          <div className="flex flex-col">
            <div className="overflow-y-auto">
              <TableBodyComponent
                title={`Sales Per Consumption Band`}
                exportFileName={`Sales Per ${
                  searchParams?.region ? "District" : "Region"
                } For ${getPeriod(searchParams)}`}
                exportTypes={[
                  "EXCEL",
                  // "PDF"
                ]}
                noSpace={true}
                loading={loading && ![4, 6].includes(networkStatus)}
                data={{
                  ...records,
                  rows: dataRows,
                }}
                renderColumns={() => (
                  <>
                    <tr className="hidden print:table-row">
                      <th
                        scope="col"
                        colSpan={1 + Headers.length}
                        className="sticky top-0 border-t border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-center text-md font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                      >
                        Customers Per{" "}
                        {searchParams?.region ? "District" : "Region"} For{" "}
                        {getPeriod(searchParams)}
                      </th>
                    </tr>
                    <tr>
                      <th
                        scope="col"
                        className="sticky top-0 left-0 z-10 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                      >
                        CLASS
                      </th>
                      {Headers.map((header) => (
                        <th
                          key={header.key}
                          scope="col"
                          className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                        >
                          {header.label}
                        </th>
                      ))}
                    </tr>
                  </>
                )}
                renderLoader={() => (
                  <tr className="border-t border-gray-200 print:border-gray-700">
                    <td className="px-4 py-4 border-b border-gray-200 print:border-gray-700">
                      <Shimmers.SingleShimmer />
                    </td>
                    <td className="px-4 py-4 border-b border-gray-200 print:border-gray-700">
                      <Shimmers.SingleShimmer />
                    </td>
                    {Headers.map((header) => (
                      <td
                        key={header.key}
                        className="px-4 py-4 border-b border-gray-200 print:border-gray-700"
                      >
                        <Shimmers.SingleShimmer />
                      </td>
                    ))}
                  </tr>
                )}
                renderItem={(item) => (
                  <tr
                    key={item._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                  >
                    <td className="whitespace-nowrap sticky left-0 z-10 bg-white px-4 py-4 text-normal text-gray-700 border-b border-gray-200 print:border-gray-700">
                      {item._id === "space" ? "" : item?.identifier || "N/A"}
                    </td>

                    {Headers.map((header) => (
                      <td className="whitespace-nowrap px-4 py-4 text-normal text-right text-gray-700 border-b border-gray-200 print:border-gray-700">
                        {item._id === "space"
                          ? ""
                          : numeral(lodash.get(item, header.key, 0)).format(
                              header.format,
                            ) || "N/A"}
                      </td>
                    ))}
                  </tr>
                )}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
